import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Calendar as CalendarIcon, MapPin, Clock, ChevronRight, Sparkles, X, Users } from "lucide-react";
import { format, isSameDay } from "date-fns";

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "h:mm a");
    } catch {
      return "";
    }
  };

  // Get dates that have events
  const eventDates = events.map(e => {
    try {
      return new Date(e.date);
    } catch {
      return null;
    }
  }).filter(Boolean);

  // Get events for selected date
  const eventsForSelectedDate = events.filter(e => {
    try {
      return isSameDay(new Date(e.date), selectedDate);
    } catch {
      return false;
    }
  });

  // Upcoming events
  const upcomingEvents = events.filter(e => {
    try {
      return new Date(e.date) >= new Date();
    } catch {
      return false;
    }
  }).slice(0, 5);

  return (
    <div className="bg-stone-50">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900"></div>
        <div className="pattern-overlay absolute inset-0"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Mark Your Calendar
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Events Calendar
            </h1>
            <p className="text-xl text-primary-100 leading-relaxed">
              Stay updated with our upcoming events, programs, and community gatherings. Join us in building a stronger community.
            </p>
          </div>
        </div>
      </section>

      {/* Events Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-1">
              <Card className="bento-card p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                    <CalendarIcon className="w-5 h-5 text-primary-900" />
                  </div>
                  Select Date
                </h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-xl border-0"
                  modifiers={{
                    hasEvent: eventDates
                  }}
                  modifiersStyles={{
                    hasEvent: { 
                      fontWeight: 'bold',
                      backgroundColor: '#D1FAE5',
                      color: '#064E3B',
                      borderRadius: '50%'
                    }
                  }}
                  data-testid="events-calendar"
                />
                <div className="mt-4 p-3 bg-primary-50 rounded-xl">
                  <p className="text-sm text-primary-700 text-center flex items-center justify-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-primary-500"></span>
                    Dates with events
                  </p>
                </div>
              </Card>
            </div>

            {/* Events List */}
            <div className="lg:col-span-2 space-y-8">
              {/* Events for Selected Date */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Selected Date</p>
                    <h2 className="font-heading text-2xl font-bold text-gray-900">
                      {format(selectedDate, "MMMM d, yyyy")}
                    </h2>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-primary-900">{eventsForSelectedDate.length}</span>
                    <p className="text-sm text-gray-500">event{eventsForSelectedDate.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                
                {eventsForSelectedDate.length === 0 ? (
                  <Card className="p-12 text-center bg-stone-50 border-dashed rounded-3xl">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <CalendarIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">No Events</h3>
                    <p className="text-gray-500">No events scheduled for this date. Check our upcoming events below!</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {eventsForSelectedDate.map((event) => (
                      <Card 
                        key={event.event_id} 
                        className="bento-card overflow-hidden cursor-pointer group"
                        onClick={() => setSelectedEvent(event)}
                        data-testid={`event-${event.event_id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-5">
                            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col items-center justify-center flex-shrink-0 shadow-lg">
                              <span className="text-3xl font-bold text-white">{format(new Date(event.date), "d")}</span>
                              <span className="text-sm text-primary-200 uppercase">{format(new Date(event.date), "MMM")}</span>
                            </div>
                            <div className="flex-grow min-w-0">
                              <h3 className="font-heading text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-900 transition-colors">
                                {event.title}
                              </h3>
                              <p className="text-gray-600 line-clamp-2 mb-4">{event.description}</p>
                              <div className="flex flex-wrap gap-4">
                                <span className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                  <Clock className="w-4 h-4" />
                                  {formatTime(event.date)}
                                </span>
                                <span className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                  <MapPin className="w-4 h-4" />
                                  {event.location}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-6 h-6 text-gray-300 group-hover:text-primary-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Upcoming Events */}
              <div>
                <div className="mb-6">
                  <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Coming Soon</p>
                  <h2 className="font-heading text-2xl font-bold text-gray-900">Upcoming Events</h2>
                </div>
                
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Card key={i} className="p-6 animate-pulse rounded-2xl">
                        <div className="flex gap-4">
                          <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                          <div className="flex-grow">
                            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : upcomingEvents.length === 0 ? (
                  <Card className="p-12 text-center bg-stone-50 border-dashed rounded-3xl">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">No Upcoming Events</h3>
                    <p className="text-gray-500">Check back later for new events!</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {upcomingEvents.map((event) => (
                      <Card 
                        key={event.event_id} 
                        className="p-5 rounded-2xl hover:shadow-lg transition-all cursor-pointer group border border-gray-100"
                        onClick={() => setSelectedEvent(event)}
                        data-testid={`upcoming-event-${event.event_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex flex-col items-center justify-center flex-shrink-0">
                            <span className="text-lg font-bold text-primary-900">{format(new Date(event.date), "d")}</span>
                            <span className="text-xs text-primary-700 uppercase">{format(new Date(event.date), "MMM")}</span>
                          </div>
                          <div className="flex-grow min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate group-hover:text-primary-900 transition-colors">
                              {event.title}
                            </h4>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedEvent(null)}
        >
          <Card 
            className="w-full max-w-lg rounded-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-primary-900 to-primary-800 p-8 text-white relative">
              <Button 
                variant="ghost" 
                size="icon"
                className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                onClick={() => setSelectedEvent(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold">{format(new Date(selectedEvent.date), "d")}</span>
                  <span className="text-sm text-primary-200 uppercase">{format(new Date(selectedEvent.date), "MMM")}</span>
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold mb-1">{selectedEvent.title}</h2>
                  <p className="text-primary-200">{format(new Date(selectedEvent.date), "EEEE, MMMM d, yyyy")}</p>
                </div>
              </div>
            </div>
            <CardContent className="p-8">
              <p className="text-gray-600 mb-6 leading-relaxed">{selectedEvent.description}</p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary-900" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-semibold text-gray-900">{formatTime(selectedEvent.date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary-900" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-semibold text-gray-900">{selectedEvent.location}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Events;
